<template>
  <div class="position-relative">
    <div ref="resizeLine" class="resize-line d-none" />

    <table
      ref="tableRef"
      v-mousedown-outside="() => (tableUtilityStore.selectedIsActive = false)"
      class="products"
      :class="{
        'edit-mode': bulkStore.editMode,
        dragging: tableUtilityStore.tableSelection.selectionDraggingState === 'normal',
        'copy-dragging': tableUtilityStore.tableSelection.selectionDraggingState === 'column',
        loading: productsStore.loading.size,
      }"
    >
      <!-- Header row -->
      <thead>
        <tr class="vertically-sticky">
          <th
            id="header"
            :ref="(el) => (columnRefs[0] = el as HTMLElement)"
            class="index"
            @click="productsStore.currentTotalProducts ? gotoRowPrompt() : undefined"
          />

          <th
            v-for="(property, columnIndex) of bulkStore.combinedProperties"
            :key="columnIndex"
            :ref="(el) => (columnRefs[columnIndex + 1] = el as HTMLElement)"
            :data-index="columnIndex >= bulkStore.stickyProperties.length ? columnIndex : undefined"
            class="table-header"
            :class="[
              {
                'horizontally-sticky': !!getStickyColumnStyle(columnIndex),
                sku: isSkuProperty(property),
                'sticky-shadow': tableUtilityStore.isSideScrolled && columnIndex + 1 === bulkStore.stickyProperties.length,
                'active-col': tableUtilityStore.tableSelection.columnIsSelected(columnIndex),
                active: tableUtilityStore.contextMenu?.id === property.defaultHeader,
                focused: focusedColumn === columnIndex,
              },
            ]"
            :style="`${getStickyColumnStyle(columnIndex)}; min-width: ${getColumnSize(property)}; max-width: ${getColumnSize(property)}`"
            @contextmenu="tableUtilityStore.openHeaderContextMenu($event, property)"
          >
            <template v-if="tableColumnsIntersectionObserver.elementIsVisible(columnIndex) || columnIndex < bulkStore.stickyProperties.length">
              <PropertyLabelComponent :property="property" show-content-label class="d-inline-flex" info-style="margin-top: 2.1em" />
              <i
                v-if="route.query.sortFieldPath === property.sortFieldPath || (!route.query.sortFieldPath && isSkuProperty(property))"
                class="fa sort-arrow"
                :class="route.query.sortOrder === 'desc' ? 'fa-long-arrow-down' : 'fa-long-arrow-up'"
                :title="route.query.sortOrder === 'desc' ? appTranslate('Sorted descending') : appTranslate('Sorted ascending')"
              />
              <div class="col-resize" @mousedown="startColumnResize($event, property, resizeLine, updateStickyColumns)" />
            </template>

            <template v-else>
              <!-- placeholder (searchable) -->
              {{ property.header }}
            </template>
          </th>
        </tr>
      </thead>

      <!-- Row index + data rows -->
      <tbody>
        <TableRow
          v-for="(product, rowIndex) of bulkStore.sessionProducts"
          :id="getRowId(rowIndex)"
          :key="(product?.sku || '') + rowIndex"
          :ref="(el) => (rowRefs[rowIndex] = el as ComponentPublicInstance)"
          v-model:focused-index-row="focusedIndexRow"
          v-model:hovered-index-row="hoveredIndexRowModel"
          :product="product"
          :row-index="rowIndex"
          :data-index="rowIndex"
          class="product"
          :class="{
            'focused-row': tableUtilityStore.focusedRows?.has(rowIndex),
            'skeleton missing': !product,
          }"
          :render-full-row="productRowsIntersectionObserver.elementIsVisible(rowIndex)"
          :get-sticky-column-style="getStickyColumnStyle"
          :should-render-column="tableColumnsIntersectionObserver.elementIsVisible"
          :first-visible-property-index="tableColumnsIntersectionObserver.visible.value?.min || 0"
          :visible-properties="visibleProperties"
          @focus-cell="focusPropertyComponent($event.value)"
          @selection="ensureInView($event.value, 'smooth')"
        />

        <TableRow
          v-for="(_, rowIndex) of bulkStore.skeletonRows"
          :key="rowIndex"
          :ref="(el) => (rowRefs[bulkStore.sessionProducts.length + rowIndex] = el as ComponentPublicInstance)"
          class="skeleton"
          :data-index="bulkStore.sessionProducts.length + rowIndex"
          :row-index="bulkStore.sessionProducts.length + rowIndex"
          :get-sticky-column-style="getStickyColumnStyle"
          :sticky-left-margins="stickyLeftMargins"
          :should-render-column="tableColumnsIntersectionObserver.elementIsVisible"
          :first-visible-property-index="tableColumnsIntersectionObserver?.visible.value?.min || 0"
          :visible-properties="visibleProperties"
          render-full-row
        />
        <!-- :render-full-row="skeletonRowsIntersectionObserver.elementIsVisible(bulkStore.sessionProducts.length + rowIndex)" -->

        <tr v-if="bulkStore.editMode" id="add-new-rows-placeholder">
          <td />
        </tr>
      </tbody>

      <tfoot v-if="collectionsInActiveFiltersHtml">
        <tr class="title-row">
          <th colspan="3" :style="getStickyColumnStyle(0)">
            <a
              v-if="!productsStore.expiredProducts && productsStore.allProductsLoaded"
              class="d-inline-block mt-4 ml-2"
              @click="productsStore.fetchExpiredProducts()"
              v-html="appTranslate('Show products expired for %s', [collectionsInActiveFiltersHtml])"
            />
            <span v-else-if="productsStore.expiredProducts?.length" v-html="appTranslate('Expired for %s', [collectionsInActiveFiltersHtml], 'uppercase')" />
          </th>
        </tr>

        <TableRow
          v-for="(product, rowIndex) of productsStore.expiredProducts"
          :id="getRowId(bulkStore.totalRows + rowIndex)"
          :key="(product.sku || '') + bulkStore.totalRows + rowIndex"
          :ref="(el) => (rowRefs[bulkStore.totalRows + rowIndex] = el as ComponentPublicInstance)"
          v-model:focused-index-row="focusedIndexRow"
          v-model:hovered-index-row="hoveredIndexRowModel"
          :product="product"
          :data-index="bulkStore.totalRows + rowIndex"
          :row-index="bulkStore.totalRows + rowIndex"
          class="product"
          :class="{
            'focused-row': tableUtilityStore.focusedRows?.has(bulkStore.totalRows + rowIndex),
          }"
          :render-full-row="productRowsIntersectionObserver.elementIsVisible(bulkStore.totalRows + rowIndex)"
          :get-sticky-column-style="getStickyColumnStyle"
          :should-render-column="tableColumnsIntersectionObserver.elementIsVisible"
          is-protected
          :first-visible-property-index="tableColumnsIntersectionObserver?.visible.value?.min || 0"
          :visible-properties="visibleProperties"
          @selection="ensureInView($event.value, 'smooth')"
        />
      </tfoot>
    </table>

    <LoaderComponent v-if="productsStore.loading.has('startingPoints') || productsStore.loading.has('restore')" :text="appTranslate('Loading...')" />

    <ContextMenuComponent
      v-if="tableUtilityStore.contextMenu"
      :position="tableUtilityStore.contextMenu.position"
      :options="tableUtilityStore.contextMenu.options"
      class="context-menu"
      :max-height="`${contextMenuMaxHeight}px`"
      :width="`${contextMenuWidth}px`"
      @close="tableUtilityStore.contextMenu = undefined"
      @contextmenu.prevent
    />

    <div
      v-for="(popOver, i) of tableUtilityStore.popOvers"
      :key="i"
      v-click-outside="() => tableUtilityStore.clearPopOvers()"
      class="pop-over"
      :style="`top: ${popOver.y}px; left: ${popOver.x}px;`"
      @click="tableUtilityStore.clearPopOvers()"
    >
      {{ popOver.msg }}

      <template v-if="popOver.list?.length">
        <div class="list minimalistic" @wheel.stop="handlePopOverWheelEvent">
          <div v-for="(item, j) of popOver.list" :key="j" v-text="item" />
        </div>
      </template>
    </div>

    <PromptComponent
      v-if="tableUtilityStore.productsPendingDeletion && store.currentUser?.role === 'superAdmin'"
      action="delete"
      :prompt="{ confirmString: 'DELETE', action: 'delete' }"
      @close="tableUtilityStore.productsPendingDeletion = undefined"
      @confirm="tableUtilityStore.deletePendingProducts()"
    >
      <template #header>
        <h5>{{ appTranslate('Do you want to delete %s products?', [tableUtilityStore.productsPendingDeletion.length]) }}</h5>
      </template>
      <p>{{ appTranslate('Applies to %s', [tableUtilityStore.productsPendingDeletion.join(', ')]) }}</p>
    </PromptComponent>

    <Teleport
      v-if="
        focusedPreview?.product &&
        !tableUtilityStore.tableSelection.multipleIndicesSelected &&
        !tableUtilityStore.contextMenu &&
        productRowsIntersectionObserver.elementIsVisible(focusedPreview.index)
      "
      :to="`#${getRowId(focusedPreview.index)} .index`"
      defer
    >
      <ImagePreview
        :key="focusedPreview.product.sku"
        :sku="focusedPreview.product.sku"
        :availability-code="focusedPreview.product.availabilityCode"
        @mousedown.stop="(focusedIndexRow = undefined), (hoveredIndexRow = undefined)"
      />
    </Teleport>

    <Teleport
      v-if="
        hoveredPreview?.product &&
        hoveredPreview?.product.sku !== focusedPreview?.product.sku &&
        !tableUtilityStore.contextMenu &&
        !tableUtilityStore.tableSelection.selectionDraggingState
      "
      class="position-absolute"
      :to="`#${getRowId(hoveredPreview.index)} .index`"
      defer
    >
      <ImagePreview
        :key="hoveredPreview.product.sku"
        :sku="hoveredPreview.product.sku"
        :availability-code="hoveredPreview.product.availabilityCode"
        transition
      />
    </Teleport>

    <!---------------------------->
    <!-- Advanced search window -->
    <!---------------------------->
    <Teleport v-if="loaded" to="#table-search-placeholder" defer>
      <TableSearch
        v-model:focused-column="focusedColumn"
        :active-cell="tableUtilityStore.tableSelection.activeCell"
        @visit="
          ensureInView($event.cell, $event.scrollBehaviour, $event.scrollMarginX, $event.scrollMarginY),
            $event.cell.rowIndex !== undefined && $event.cell.columnIndex !== undefined
              ? tableUtilityStore.tableSelection.setCellSelection($event.cell)
              : undefined
        "
      />
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import ContextMenuComponent from '@/components/ContextMenuComponent.vue'
import TableRow from '@/components/products/archive/table/TableRow.vue'
import PropertyLabelComponent from '@/components/products/PropertyLabelComponent.vue'
import PromptComponent from '@/components/PromptComponent.vue'
import LoaderComponent from '@/components/simple/LoaderComponent.vue'
import { useIntersectionObserver } from '@/composables/useIntersectionObserver'
import { useTableColumnResize } from '@/composables/useTableColumnResize'
import { useTranslation } from '@/composables/useTranslation'
import { Cell, Coord } from '@/models/GenericInterfaces'
import { useDefaultStore } from '@/store'
import { useProductsStore } from '@/store/productsArchiveStore'
import { useProductsBulkStore } from '@/store/productsBulkStore'
import { useProductsTableUtilityStore } from '@/store/productsTableUtilityStore'
import { useProductsViewStore } from '@/store/productsViewStore'
import { getRowId } from '@/utils/bulkUtils'
import { isSkuProperty, propertyHasStaticValues } from '@/utils/propertyUtils'
import { clearTextSelection, clone, getScrollbarWidth, getVerticalScrollbarHeight } from '@/utils/tools'
import { ComponentPublicInstance, computed, nextTick, onActivated, onDeactivated, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import ImagePreview from './ImagePreview.vue'
import TableSearch from './TableSearch.vue'
import Message from '@/models/Message'
import { usePropertyStore } from '@/store/propertyStore'

// -----------
// -- Setup --
// -----------
const props = withDefaults(
  defineProps<{
    stickyTopMargin?: number
  }>(),
  {
    stickyTopMargin: 0,
  },
)

const store = useDefaultStore()
const productsStore = useProductsStore()
const bulkStore = useProductsBulkStore()
const productsViewStore = useProductsViewStore()
const tableUtilityStore = useProductsTableUtilityStore()
const propertyStore = usePropertyStore()

const route = useRoute()
const { appTranslate } = useTranslation()
const { getColumnSize, startColumnResize, resetColumnSize, columnSizesUpdated } = useTableColumnResize()

const productRowsIntersectionObserver = useIntersectionObserver(true)
const tableColumnsIntersectionObserver = useIntersectionObserver(true)
const skeletonRowsIntersectionObserver = useIntersectionObserver()

const getComputedPreview = (index?: number) => {
  if (index !== undefined) {
    const product = bulkStore.allProducts[index]
    if (product) {
      const productInput = bulkStore.currentState[product.sku]

      return {
        product: {
          sku: productInput?.sku || product.sku,
          availabilityCode: productInput?.availability?.code ?? product?.availability?.code,
        },
        index,
      }
    }
  }
  return undefined
}

// -------------
// -- Statics --
// -------------
const contextMenuWidth = 300
const contextMenuMaxHeight = 500

let preFetchScrollY = window.scrollY
let scrollInterval: number
const scrolling = { x: 0, y: 0 }

// ---------------
// -- Reactives --
// ---------------
const tableRef = ref<HTMLElement>()
const resizeLine = ref<HTMLElement>()
const stickyLeftMargins = ref<number[]>([])

const focusedIndexRow = ref<number>()
const hoveredIndexRow = ref<number>()
let hoverTimeout = 0

const columnRefs = ref<{ [index: number]: HTMLElement }>({})
const rowRefs = ref<{ [index: number]: ComponentPublicInstance }>({})

const tableBounds = ref<Record<'outerLeft' | 'innerLeft' | 'right' | 'top' | 'bottom', number>>()

const loaded = ref(false) // necessary for Teleport <TableSearch>

// --------------------
// -- Computed props --
// --------------------
const focusedPreview = computed(() => getComputedPreview(focusedIndexRow.value))
const hoveredPreview = computed(() => getComputedPreview(hoveredIndexRow.value))

const affectsStickyColumns = computed(() => [
  bulkStore.selectedProperties,
  productsViewStore.productSideMenuWidth,
  productsViewStore.fullscreen,
  columnSizesUpdated.value,
])

const collectionsInActiveFiltersHtml = computed(
  () => productsStore.collectionsWithRelations?.map((collection) => `<em>${collection.name}</em>`).join(' & ') || '',
)

const hoveredIndexRowModel = computed({
  get: () => hoveredIndexRow.value,
  // shows image after small timeout
  set: (rowIndex?: number) => {
    clearTimeout(hoverTimeout)
    hoveredIndexRow.value = undefined

    if (rowIndex !== undefined) {
      hoverTimeout = window.setTimeout(() => {
        hoveredIndexRow.value = rowIndex
      }, 100)
    }
  },
})

const visibleProperties = computed(() => {
  if (tableColumnsIntersectionObserver.visible.value) {
    const from = tableColumnsIntersectionObserver.visible.value.min - bulkStore.stickyProperties.length
    const to = tableColumnsIntersectionObserver.visible.value.max - bulkStore.stickyProperties.length

    return bulkStore.selectedProperties?.slice(from, to) || []
  }
  return []
})

const focusedColumn = ref<number>() // result from column search

const stickyTopMarginPx = computed(() => props.stickyTopMargin + `px`)
const indexMarginPx = computed(() => stickyLeftMargins.value[0] + `px`)

// --------------
// -- Watchers --
// --------------
watch(affectsStickyColumns, () => updateStickyColumns())

watch(route, (newRoute) => {
  if (newRoute.name === 'products') {
    tableUtilityStore.tableSelection.clearSelected()
    tableUtilityStore.clearCopied(false)
    focusedIndexRow.value = undefined
  }
})

watch(
  () => productsStore.loading.size === 0,
  () => {
    setTimeout(() => {
      if (tableUtilityStore.tableSelection.activeCell && bulkStore.allProducts.length < tableUtilityStore.tableSelection.activeCell.rowIndex) {
        tableUtilityStore.tableSelection.clearSelected()
      }
    }, 500)
  },
)

watch(
  () => bulkStore.selectedProperties,
  (_, prevValue) => {
    if (prevValue) {
      if (document.hasFocus()) {
        tableUtilityStore.clearCopied()
      } else {
        window.addEventListener(
          'focus',
          () => {
            tableUtilityStore.clearCopied()
          },
          { once: true },
        )
      }
    }
  },
)

watch(
  () => tableUtilityStore.tableSelection.activeCell,
  (newCell, previousCell) => {
    // watch for cell switch
    if (
      tableUtilityStore.tableSelection.selectedCells.length === 1 &&
      (newCell?.columnIndex !== previousCell?.columnIndex || newCell?.rowIndex !== previousCell?.rowIndex)
    ) {
      tableUtilityStore.selectedIsActive = false
    }

    clearTextSelection() // to allow copy from cell(s)
  },
)

watch(
  () => bulkStore.sessionProducts,
  () => {
    observeProductRowIntersections()
    observeSkeletonIntersections()
  },
)

watch(
  () => productsStore.expiredProducts,
  () => {
    observeProductRowIntersections()
  },
)

watch([() => bulkStore.selectedCultures.length, () => bulkStore.selectedProperties], () => {
  observeTableColumnIntersections()
})

watch(
  () => tableUtilityStore.tableSelection.selectionDraggingState,
  (value) => {
    if (value) {
      mapTableBounds()
      document.addEventListener('mousemove', mouseMoveHandler)
    } else {
      document.removeEventListener('mousemove', mouseMoveHandler)
      stopScroll()
    }
  },
)

watch(
  () => bulkStore.properties,
  (_newValue, prevValue) => {
    if (prevValue) {
      // re-init on property update
      tableColumnsIntersectionObserver.initIntersectionObserver()
      observeTableColumnIntersections()
    }
  },
)

// -----------------------
// -- Lifecycle && init --
// -----------------------
onMounted(() => {
  activateView()
  updateStickyColumns() // for hot reloads
})
onActivated(() => activateView())
onDeactivated(() => deactivateView())
onUnmounted(() => deactivateView())

const shiftIsPressed = ref(false)

const activateView = () => {
  tableColumnsIntersectionObserver.initIntersectionObserver()
  productRowsIntersectionObserver.initIntersectionObserver()
  observeTableColumnIntersections()
  observeProductRowIntersections()
  observeSkeletonIntersections()

  window.addEventListener('scroll', sideScrollHandler)
  window.addEventListener('keydown', keydownHandler)
  window.addEventListener('keyup', keyupHandler)
  window.addEventListener('paste', pasteHandler)
  store.fullScreenSticky = true

  nextTick().then(() => {
    loaded.value = true
  })
}
const deactivateView = () => {
  tableColumnsIntersectionObserver.disconnectIntersectionObserver()
  productRowsIntersectionObserver.disconnectIntersectionObserver()
  skeletonRowsIntersectionObserver.disconnectIntersectionObserver()
  window.removeEventListener('scroll', sideScrollHandler)
  window.removeEventListener('keydown', keydownHandler)
  window.removeEventListener('keyup', keyupHandler)
  window.removeEventListener('paste', pasteHandler)
  store.fullScreenSticky = false
}

const observeTableColumnIntersections = async () => {
  await nextTick()
  const elements = tableRef.value?.querySelectorAll('thead th[data-index]')
  tableColumnsIntersectionObserver.observeAll(elements)
}

const observeProductRowIntersections = async () => {
  await nextTick()
  const elements = tableRef.value?.querySelectorAll('tr.product[data-index]')
  productRowsIntersectionObserver.observeAll(elements)
}

const observeSkeletonIntersections = async () => {
  await nextTick()
  // seems to be necessary to re-initialize skeleton observer for all rows to load properly
  skeletonRowsIntersectionObserver.initIntersectionObserver(undefined, undefined, async (index?: number, element?: Element) => {
    preFetchScrollY = window.scrollY
    const lastProductRow = productsStore.currentProducts.length - 1
    const isMissingRow = element?.classList.contains('missing')

    const fetched = await productsStore.loadProducts(index, isMissingRow ? 150 : undefined)
    if (fetched && !productRowsIntersectionObserver.elementIsVisible(lastProductRow) && !isMissingRow && window.scrollY > preFetchScrollY) {
      window.scrollTo({ top: preFetchScrollY })
    }
  })

  const elements = tableRef.value?.querySelectorAll('tr.skeleton[data-index]')
  skeletonRowsIntersectionObserver.observeAll(elements)
}

const mouseMoveHandler = (event: MouseEvent) => {
  if (tableBounds.value) {
    // ##############
    // ## Method 2 ## arbitrary scroll on timeout, choppy but works
    // ##############
    let x = 0
    let y = 0

    const onlyStickiesIsSelected =
      tableUtilityStore.tableSelection.orderedSelectionData &&
      tableUtilityStore.tableSelection.orderedSelectionData?.rightmostColumn <= bulkStore.stickyProperties.length - 1

    if (tableUtilityStore.tableSelection.selectionDraggingState !== 'column') {
      if (event.x < (onlyStickiesIsSelected ? tableBounds.value.outerLeft : tableBounds.value.innerLeft)) {
        x = -1
      } else if (event.x > tableBounds.value.right) {
        x = 1
      }
    }

    if (tableUtilityStore.tableSelection.selectionDraggingState !== 'row') {
      if (event.y < tableBounds.value.top) {
        y = -1
      } else if (event.y > tableBounds.value.bottom) {
        y = 1
      }
    }

    if (x || y) {
      if ((scrollInterval && x !== scrolling.x) || y !== scrolling.y) {
        // change scroll direction during scroll
        stopScroll()
        scroll({ x, y })
      }
      if (!scrollInterval) {
        tableUtilityStore.clearPopOvers()
        scrolling.x = x
        scrolling.y = y
        scrollInterval = window.setInterval(() => {
          scroll({ x, y })
        }, 50)
      }
    } else {
      scrollInterval && stopScroll()
    }

    // ##############
    // ## Method 3 ## selects and scrolls to next cell
    // ##############
    // let deltaX = 0,
    //   deltaY = 0
    // if (tableUtilityStore.tableSelection.selectionDraggingState !== 'column') {
    //   if (event.x < tableBounds.value.left) {
    //     deltaX = -1
    //   } else if (event.x > tableBounds.value.right) {
    //     deltaX = 1
    //   }
    // }

    // if (tableUtilityStore.tableSelection.selectionDraggingState !== 'row') {
    //   if (event.y < tableBounds.value.top) {
    //     deltaY = -1
    //   } else if (event.y > tableBounds.value.bottom) {
    //     deltaY = 1
    //   }
    // }

    // if (deltaX || deltaY) {
    //   if (!scrollInterval) {
    //     const maxColIndex = (bulkStore.selectedProperties?.length || 1) - 1
    //     const maxRowIndex = bulkStore.totalRows //bulkStore.allProducts.length - 1

    //     // TODO duplicated code from keydownhandler navigation (but doesn't handle expired products)
    //     scrollInterval = window.setInterval(
    //       () => {
    //         const cell = tableUtilityStore.tableSelection.expandOrContractSelection(deltaX, deltaY, maxRowIndex, maxColIndex)
    //         cell && ensureInView(cell, 'instant', 0, 0, true)
    //       },
    //       deltaX ? 200 : 75,
    //     )
    //   }
    // } else {
    //   scrollInterval && stopScroll()
    // }
  }
}

// ---------------
// -- Functions --
// ---------------

const verticalScrollPerInterval = 26
const horizontalScrollPerInterval = 50 //verticalScrollPerInterval * 1.5

const scroll = (coord: Coord) => {
  if (tableBounds.value) {
    tableBounds.value.top = columnRefs.value[0]?.getBoundingClientRect().bottom
  }
  window.scrollBy({ left: coord.x * horizontalScrollPerInterval, top: coord.y * verticalScrollPerInterval })
}

const stopScroll = () => {
  window.clearInterval(scrollInterval)
  scrollInterval = 0
}

const focusPropertyComponent = async (cell?: Cell, focusedByKeyBoard?: boolean) => {
  if (!cell) {
    return
  }

  const propertyComponent = tableUtilityStore.propertyComponentRefs.get(cell)
  if (!propertyComponent) {
    return
  }

  tableUtilityStore.selectedIsActive = true

  await nextTick()
  propertyComponent.focusField(focusedByKeyBoard)
}

const updateStickyColumns = async () => {
  let left = 0
  await nextTick()

  stickyLeftMargins.value = Array.from({
    length: bulkStore.stickyProperties.length + 1,
  }).map((_, i) => {
    const prevRef = columnRefs.value[i - 1]
    left += prevRef?.getBoundingClientRect().width || 0
    return left + (productsViewStore.fullscreen ? 0 : productsViewStore.productSideMenuWidth)
  })
}

const sideScrollHandler = () => {
  tableUtilityStore.isSideScrolled = !!window.scrollX
}

const getStickyColumnStyle = (columnIndex: number) =>
  stickyLeftMargins.value[columnIndex + 1] ? `left: ${stickyLeftMargins.value[columnIndex + 1]}px` : undefined

const ensureInView = (targetCell: Partial<Cell>, scrollBehaviour: 'instant' | 'smooth' = 'instant', marginX = 1, marginY = 1) => {
  if (targetCell) {
    let scrollX: number | undefined
    let scrollY: number | undefined

    if (
      targetCell.columnIndex !== undefined &&
      targetCell.columnIndex >= bulkStore.stickyProperties.length &&
      tableUtilityStore.tableSelection.selectionDraggingState !== 'column'
    ) {
      const xTargetElement = columnRefs.value[targetCell.columnIndex + 1]
      const xTargetRect = xTargetElement?.getBoundingClientRect()

      if (xTargetRect) {
        const stickyOffsetWidth = columnRefs.value[bulkStore.stickyProperties.length]?.offsetWidth || 0
        const stickyXOffset = (stickyLeftMargins.value[bulkStore.stickyProperties.length] || 0) + stickyOffsetWidth

        // outside == not fully in view
        const isOutsideLeft = xTargetRect.left - marginX < stickyXOffset
        const isOutsideRight = xTargetRect.right + marginX > window.innerWidth

        if (isOutsideRight) {
          scrollX = xTargetRect.right - window.innerWidth + getScrollbarWidth() + marginX // + 1 triggers rendering of next column, making the scrolling feel smoother
        } else if (isOutsideLeft) {
          scrollX = xTargetRect.left - stickyXOffset - marginX
        }
      }
    }

    if (targetCell.rowIndex !== undefined) {
      const yTargetElement = rowRefs.value[targetCell.rowIndex]?.$el
      const yTargetRect = yTargetElement?.getBoundingClientRect()

      if (yTargetRect && tableUtilityStore.tableSelection.selectionDraggingState !== 'row') {
        const headerBottom = columnRefs.value[0]?.getBoundingClientRect()?.bottom || 0

        // checks to ensure target is fully in view
        const isBelowBottom = yTargetRect.bottom + marginY > window.innerHeight - getVerticalScrollbarHeight()
        const isAboveTop = yTargetRect.top - marginY < headerBottom

        if (isBelowBottom) {
          scrollY = yTargetRect.bottom - window.innerHeight + getVerticalScrollbarHeight() + marginY // + 1 is needed to trigger potential fetch
        } else if (isAboveTop) {
          scrollY = yTargetRect.top - headerBottom - marginY
        }
      }
    }

    window.scrollBy({ left: scrollX, top: scrollY, behavior: scrollBehaviour })
  }
}

const keydownHandler = async (e: KeyboardEvent) => {
  if (e.defaultPrevented) {
    return
  }

  shiftIsPressed.value = e.shiftKey

  tableUtilityStore.popOvers.length = 0

  if (!store.modalOpen && !tableUtilityStore.contextMenu) {
    const isArrowKey = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)
    const isNavKey = ['Enter', 'Tab'].includes(e.key)

    if ((isArrowKey || isNavKey) && !e.altKey) {
      e.preventDefault()
      tableUtilityStore.selectedIsActive = false

      const focusDirection = e.shiftKey ? -1 : 1
      const deltaX = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : e.key === 'Tab' ? focusDirection : 0
      const deltaY = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : e.key === 'Enter' ? focusDirection : 0

      const maxColIndex = bulkStore.combinedProperties.length - 1

      if (tableUtilityStore.tableSelection.activeCell) {
        if (isArrowKey && e.shiftKey) {
          const footerIsSelected = tableUtilityStore.tableSelection.activeCell.rowIndex >= bulkStore.sessionProducts.length
          const minRowIndex = footerIsSelected ? bulkStore.sessionProducts.length : 0
          const maxRowIndex = footerIsSelected ? bulkStore.allProducts.length - 1 : bulkStore.sessionProducts.length - 1
          const cell = tableUtilityStore.tableSelection.expandOrContractSelection(deltaX, deltaY, maxRowIndex, maxColIndex, minRowIndex)
          cell && ensureInView(cell)
        } else {
          const maxRowIndex = bulkStore.allProducts.length - 1
          await tableUtilityStore.tableSelection.navigateTable(deltaX, deltaY, maxRowIndex, maxColIndex)
          tableUtilityStore.tableSelection.activeCell && ensureInView(tableUtilityStore.tableSelection.activeCell)
        }
      }
    } else if (e.key === 'Backspace') {
      if (bulkStore.editMode) {
        tableUtilityStore.deleteSelectedCells()
      } else {
        tableUtilityStore.activeCellRect && tableUtilityStore.showNotInEditModePopOver(tableUtilityStore.activeCellRect)
      }
    } else if (e.key === 'Delete') {
      if (bulkStore.editMode) {
        if (!tableUtilityStore.selectedIsActive) {
          if (e.shiftKey) {
            tableUtilityStore.resetSelectedCells()
          } else {
            tableUtilityStore.deleteSelectedCells()
          }
        }
      } else {
        tableUtilityStore.activeCellRect && tableUtilityStore.showNotInEditModePopOver(tableUtilityStore.activeCellRect)
      }
    } else if (e.key === 'F2') {
      // focus
      if (bulkStore.editMode) {
        if (tableUtilityStore.activeCellIsEditable) {
          if (tableUtilityStore.tableSelection.selectedCells.length === 1) {
            focusPropertyComponent(tableUtilityStore.tableSelection.activeCell, true)
          }
        } else {
          tableUtilityStore.activeCellRect && tableUtilityStore.showNotWritablePopOver(tableUtilityStore.activeCellRect)
        }
      } else {
        tableUtilityStore.activeCellRect && tableUtilityStore.showNotInEditModePopOver(tableUtilityStore.activeCellRect, undefined, true)
      }

      tableUtilityStore.tableSelection.activeCell && ensureInView(tableUtilityStore.tableSelection.activeCell)
    } else if (e.key === 'Escape') {
      if (tableUtilityStore.selectedIsActive) {
        // bulkStore.undo()
      }
      tableUtilityStore.clearCopied()
      tableUtilityStore.selectedIsActive = false
      tableUtilityStore.tableSelection.abortSelectionDrag()
    } else if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey) {
      const key = e.key.toLowerCase()

      if (key === 'c') {
        tableUtilityStore.copySelectedCellsContent()
      } else if (key === 'x') {
        if (bulkStore.editMode) {
          tableUtilityStore.copySelectedCellsContent(true)
        } else {
          tableUtilityStore.activeCellRect && tableUtilityStore.showNotInEditModePopOver(tableUtilityStore.activeCellRect)
        }
        // } else if (key === 'v') {
        //   if (bulkStore.editMode) {
        //     tableUtilityStore.pasteToCells()
        //   } else {
        //     tableUtilityStore.activeCellRect && tableUtilityStore.showNotInEditModePopOver(tableUtilityStore.activeCellRect)
        //   }
      } else if (key === 'z') {
        bulkStore.undo()
        e.preventDefault()
      } else if (key === 'y') {
        bulkStore.redo()
        e.preventDefault()
      }
    } else if (e.key?.length === 1 && !e.ctrlKey && !e.metaKey && (!e.altKey || e.location === 2) && tableUtilityStore.tableSelection.activeCell) {
      // type
      if (e.key === ' ') {
        // prevent scroll
        e.preventDefault()
      }

      if (bulkStore.editMode) {
        tableUtilityStore.selectedIsActive = true

        const propertyComponent = tableUtilityStore.propertyComponentRefs.get(tableUtilityStore.tableSelection.activeCell)
        const property = bulkStore.combinedProperties?.[tableUtilityStore.tableSelection.activeCell.columnIndex]

        if (property) {
          const shouldClear =
            propertyHasStaticValues(property) &&
            tableUtilityStore.tableSelection.selectedCells.length === 1 &&
            (property.valueType === 'string' || /[0-9.,]/.test(e.key))

          await nextTick()
          propertyComponent?.focusField(true, shouldClear)
        }
      } else {
        tableUtilityStore.activeCellRect && tableUtilityStore.showNotInEditModePopOver(tableUtilityStore.activeCellRect)
      }
    }
  }
}

const keyupHandler = (e: KeyboardEvent) => {
  shiftIsPressed.value = e.shiftKey
}

const pasteHandler = async (e: ClipboardEvent) => {
  if (bulkStore.editMode) {
    tableUtilityStore.pasteToCells(e, shiftIsPressed.value)
  } else {
    tableUtilityStore.activeCellRect && tableUtilityStore.showNotInEditModePopOver(tableUtilityStore.activeCellRect)
  }
}

const mapTableBounds = () => {
  const indexColumn = columnRefs.value[0]
  const lastSticky = columnRefs.value[bulkStore.stickyProperties.length]

  tableBounds.value = {
    top: columnRefs.value[0]?.getBoundingClientRect().bottom,
    outerLeft: stickyLeftMargins.value[0] + indexColumn.getBoundingClientRect()?.width,
    innerLeft: stickyLeftMargins.value[stickyLeftMargins.value.length - 1] + lastSticky.getBoundingClientRect()?.width,
    bottom: window.innerHeight - getVerticalScrollbarHeight(),
    right: window.innerWidth - getScrollbarWidth(),
  }
}

const gotoRowPrompt = async () => {
  const input = prompt(appTranslate(`Go to row (1 - %s)`, [productsStore.currentTotalProducts]))
  if (input) {
    const rowIndex = parseInt(input) - 1
    if (rowIndex >= 0 && rowIndex < productsStore.currentTotalProducts) {
      if (!productsStore.currentProducts[rowIndex]) {
        productsStore.currentProducts[rowIndex] = undefined
      }

      await nextTick()
      ensureInView({ rowIndex }, undefined, 26, 26)
      tableUtilityStore.tableSelection.setCellSelection({ rowIndex, columnIndex: tableUtilityStore.tableSelection.activeCell?.columnIndex || 0 })
    } else {
      store.addMessage(
        new Message(
          appTranslate('Invalid row'),
          appTranslate(`%s is not a valid row. Choose a row between %s and %s`, [input, 1, productsStore.currentTotalProducts]),
          'warning',
        ),
      )
    }
  }
}

const handlePopOverWheelEvent = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  const element = target?.closest('.list')
  if (!element) {
    return
  }

  const canScroll = element.scrollHeight > element.clientHeight

  if (!canScroll) {
    e.preventDefault() // Stops page scroll
  }
}
</script>

<style lang="scss" scoped>
@use 'sass:color';

$max-cell-width: 550px;
$default-cell-width: 250px;
$row-height: 25px;

$active-colour: green;
$border-colour: #d2d2d2;
$cell-border: 1px solid $border-colour;
$selected-modified-bg: #196c0033;

$active-header-marker: $active-colour 1px;
$active-header-dark: var(--table-primary-header-colour) 1px;

$selected-border-size: 2px;
$selected-border: $selected-border-size solid $active-colour;
$copy-boundary-border: 2px dashed $active-colour;

$focused-bg: #f8f8cc; // matched search, focused row
$selected-row-colour: #d2cfff;
// $selected-index-colour: rgb(0 20 255 / 10%);

$header-z: 4;
$horizontally-sticky-header-z: 6 !important;
$horizontally-sticky-z: 2;
$add-new-rows-z: 2;
$footer-z: 1;
$sticky-footer-z: 2;
$active-before-z: 1;
$th-z: 2;

$index-z: 2;
$focused-index-z: 3;
$hovered-index-z: 4;

$active-cell-data: 3;
$selected-z: 1;
$sticky-shadow-z: 7;
$resize-line-z: 7;

// $header-z: 40;
// $horizontally-sticky-header-z: 60 !important;
// $horizontally-sticky-z: 20;
// $add-new-rows-z: 20;
// $footer-z: 10;
// $sticky-footer-z: 20;
// $active-before-z: 10;
// $th-z: 35;

// $index-z: 20;
// $focused-index-z: 30;
// $hovered-index-z: 40;

// $active-cell-data: 30;
// $selected-z: 35;
// $sticky-shadow-z: 70;
// $resize-line-z: 70;

// background, borders and pseudo elements
// th.active-row, th.active-col::before - active row/column (semi transparent grey)
// (th/td).sticky-shadow::after - sticky shadow
// td::before - selected border (solid green)
// td .data - modified/focused background (green/light yellow)
// td .data::before -selected background (semi transparent grey)
// td .data::after - copy boundary border (dashed green)

:deep() {
  table.products {
    position: relative;
    height: 100%; // necessary for td to inherit and make table data 100% height
    font-size: 14px;
    border-collapse: separate;
    border-spacing: 0;
    text-shadow: none;
    user-select: none;

    cursor: cell;
    &.loading {
      cursor: wait;
    }

    &.copy-dragging:not(.loading) {
      cursor: crosshair;
    }

    thead {
      .index,
      .horizontally-sticky {
        z-index: $horizontally-sticky-header-z;
      }

      th {
        top: 0;

        &.table-header {
          cursor: default;
          padding: 2px;
          z-index: $header-z;

          .label img {
            max-height: 1.35em;
          }
        }

        * {
          max-width: 100%;
        }

        label {
          overflow: hidden;
        }
      }
    }

    tbody::after {
      content: '';
      display: block;
      height: 3em;
    }

    #add-new-rows-placeholder {
      td {
        padding-top: 1em;
        position: sticky;
        left: v-bind(indexMarginPx);
        border: none;
        z-index: $add-new-rows-z;

        > * {
          margin: 0 1em;
          width: calc(100vw - #{v-bind(indexMarginPx)} - 3em);
        }
      }
    }

    tfoot {
      z-index: $footer-z;
      position: relative;

      // test to make footer sticky to bottom
      &.sticky-footer {
        position: sticky;
        bottom: 0;
        left: 0;
        z-index: $sticky-footer-z;
      }

      .title-row th {
        border: none;
        position: sticky;
        font-size: 1.5em;
        color: red;
        background-color: transparent;
        overflow-x: visible;
      }

      tr:nth-child(2) td {
        border-top: $cell-border;
      }
    }

    // active headers overlay
    tr.active-row .index,
    th.active-col,
    .active {
      position: relative;

      &::before {
        content: '';
        z-index: $active-before-z;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: #0002;
        pointer-events: none;
      }

      &.modified::before {
        background-color: $selected-modified-bg;
      }
    }

    tr {
      &.active-row {
        .index {
          // background-color: $header-bg-color; // disabled 241106
          border-right-color: $active-colour;

          &.modified {
            background-color: var(--modified-background);
          }
        }
      }

      &.selected-row th {
        background-color: $selected-row-colour !important;
      }

      &.focused-row {
        th,
        td:not(.modified) .data {
          background-color: $focused-bg !important;
        }
      }
    }

    .horizontally-sticky,
    .vertically-sticky th,
    .index {
      position: sticky !important;
    }

    .vertically-sticky th {
      // top: var(--sticky-top-margin);
      top: v-bind(stickyTopMarginPx);
    }

    &.copy-drag-source .data {
      border: $selected-border;
    }

    th {
      font-weight: 700;
      white-space: nowrap;
      z-index: $th-z;
      background-color: var(--table-primary-header-colour);
      border: 2px solid transparent;

      &:not(.index) {
        min-width: $default-cell-width;
        max-width: $default-cell-width;
      }

      &.active-col {
        background-color: var(--table-primary-header-colour);
        border-bottom-color: $active-colour;
        // color: $active-colour;
      }
    }

    th,
    .data {
      padding: 2px 4px;
    }

    th,
    td {
      border: $cell-border;
      border-top: none;
      border-left: none;
      // border-right: none;
      vertical-align: top;
      height: 100%; // necessary for firefox

      &,
      .data {
        min-height: 100%; // necessary for firefox
      }

      .data {
        background-color: white;

        &:focus {
          outline: none; // remove if outline is used to mark preliminary
        }
      }
    }

    th.focused,
    th.focused,
    td.focused .data {
      &,
      .sort-arrow {
        background-color: $focused-bg !important;
      }
    }

    .index {
      left: v-bind(indexMarginPx);
      z-index: $index-z;
      text-align: center;
      margin-top: 1em;
      min-width: 2.8em;
      user-select: none;

      &.focused-index {
        z-index: $focused-index-z;
      }

      &.hovered-index {
        z-index: $hovered-index-z;
      }

      &.focused-index,
      &.hovered-index {
        background-color: $selected-row-colour !important;
      }

      .index.modified,
      td.modified .data {
        background-color: var(--modified-background);
      }
    }

    .index.modified,
    td.modified .data {
      background-color: var(--modified-background);
    }

    td {
      padding: 0;
      background-color: white;

      max-width: 0; // allow overflow to be hidden

      .data {
        border: none; // creates a visible bevelled edge otherwise
        overflow: hidden;

        align-content: baseline;

        p {
          display: inline; // display paragraphs without line-breaks in v-html
        }

        &,
        &::before {
          text-overflow: ellipsis;
          word-wrap: break-word;
          white-space: nowrap;
        }

        &::before {
          border: 2px solid transparent;
          background-color: #fff;
          overflow-y: hidden;
          overflow-x: hidden; // prevent cell side scrolling

          &::-webkit-scrollbar {
            width: 2px;
          }
        }

        &:not(.static) {
          // max-height: unset;
          overflow: visible; // needed to display dropdowns correctly
          position: absolute; // works with both position static and position absolute. had problem with selection with static

          width: max-content;
          max-width: $max-cell-width;
          min-width: 100%;

          &.has-terms,
          &.simpleTaxonomy,
          &.categories {
            min-width: max(100%, $default-cell-width);
          }

          &.categories {
            width: 100%;
          }
        }

        .data-for {
          margin-right: 1px;
          object-fit: contain;
          width: 0.8rem;
          opacity: 0.9;
          max-width: 100%;
        }
      }

      &.horizontally-sticky {
        z-index: $horizontally-sticky-z;
      }

      &.active-cell .data {
        z-index: $active-cell-data; // needs to be on top of add new rows
      }

      &:not(.modified).readonly {
        .value {
          color: #888;
        }

        // .data:not(.focused) {
        .data {
          background-color: #f0f0f0;
        }
      }

      &.selected {
        position: relative;

        .copy-drag-anchor {
          position: absolute;
          cursor: crosshair;
          $size: 6px;
          width: $size;
          height: $size;
          background-color: $active-colour;
          bottom: -1px;
          right: -1px;
          transform: translate(calc(50% - 2px), calc(50% - 2px));
          border: 1px solid white;
          box-sizing: content-box;
        }

        &:not(.active-cell) {
          .data::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: #0002;
          }

          &.modified .data::before {
            background-color: $selected-modified-bg;
          }
        }

        &::before,
        .data:not(.static)::before {
          content: '';
          position: absolute;
          top: -1px;
          left: -1px;
          right: -1px;
          bottom: -1px;
          width: calc(100% + 2px);
          background-color: transparent;
          pointer-events: none;
        }

        &.first-non-sticky::before {
          left: 0;
          width: calc(100% + 1px);
        }

        &.topmost-selected::before,
        &.topmost-selected .data:not(.static)::before {
          // &.topmost-selected .data::before {
          border-top: $selected-border;
        }
        &.bottommost-selected::before,
        &.bottommost-selected .data:not(.static)::before {
          // &.bottommost-selected .data::before {
          border-bottom: $selected-border;
        }
        &.leftmost-selected::before,
        &.leftmost-selected .data:not(.static)::before {
          // &.leftmost-selected .data::before {
          border-left: $selected-border;
        }
        &.rightmost-selected::before,
        &.rightmost-selected .data:not(.static)::before {
          // &.rightmost-selected .data::before {
          border-right: $selected-border;
        }
      }

      &.copied {
        position: relative;

        &.cut .data::after {
          background-color: #fafafaaa !important;
        }

        .data::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;

          background-color: transparent;
          pointer-events: none;
        }

        &.topmost-selected.topmost-copy-boundary {
          .data::after {
            top: 1px;
            height: calc(100% - 1px);
          }

          &.bottommost-selected.bottommost-copy-boundary .data::after {
            height: calc(100% - 2px);
          }
        }
        &.leftmost-selected.leftmost-copy-boundary {
          .data::after {
            left: 1px;
            width: calc(100% - 1px);
          }

          &.rightmost-selected.rightmost-copy-boundary .data::after {
            width: calc(100% - 2px);
          }
        }

        &.bottommost-selected.bottommost-copy-boundary .data::after {
          height: calc(100% - 1px);
        }
        &.rightmost-selected.rightmost-copy-boundary .data::after {
          width: calc(100% - 1px);
        }

        &.topmost-copy-boundary .data::after {
          border-top: $copy-boundary-border;
        }

        &.bottommost-copy-boundary .data::after {
          border-bottom: $copy-boundary-border;
        }

        &.leftmost-copy-boundary .data::after {
          border-left: $copy-boundary-border;
        }

        &.rightmost-copy-boundary .data::after {
          border-right: $copy-boundary-border;
        }
      }
    }

    .sticky-shadow::after {
      content: '';
      $width: 9px;
      position: absolute;

      right: calc(#{$width} * -1);
      z-index: $sticky-shadow-z;

      top: 0;
      width: $width;
      height: calc(100% + 1px);
      background: linear-gradient(to right, #00000025, transparent);
    }

    .skeleton td {
      position: relative;

      &::before {
        content: '';
        position: absolute;
        background-color: #ddd;
        margin: auto;
        width: 18px;
        height: 5px;
        text-align: center;

        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
      }
    }
  }
}

// .link:hover {
//   color: blue;
//   text-decoration: underline;
// }

.context-menu {
  .item {
    display: block;
    padding: 0.2em 0.5em;

    &:hover {
      background-color: #eee;
    }
  }

  .option :disabled {
    opacity: 0.5;
  }
}

.min-w-100 {
  min-width: 100%;
}

.pop-over {
  position: fixed;
  z-index: 1070; // bootstrap popover default
  font-size: 13px;
  white-space: pre-line;
  padding: 3px;
  background-color: color.scale($focused-bg, $lightness: 3%);
  border: 1px solid grey;
  user-select: none;
  box-shadow: 1px 1px 5px #0004;

  .list {
    margin-top: 0.5em;
    max-height: 200px;
    overflow-y: auto;
    overscroll-behavior: none;
    border-top: 2px solid #0001;
    background-color: #0001;
  }
}

.sort-arrow {
  position: absolute;
  right: 0;
  padding: 0 4px 0 2px;
  top: 50%;
  transform: translateY(-50%);
  background-color: var(--table-primary-header-colour);
}

.col-resize {
  position: absolute;
  top: 0;
  right: 0;
  width: 5px;
  height: 100%;
  cursor: col-resize;

  &:hover {
    background-color: grey;
  }

  &::before {
    position: absolute;
    width: 1px;
    color: grey;
  }
}

.resize-line {
  position: absolute;
  top: 0;
  z-index: $resize-line-z;
  height: 100%;
  width: 1px;
  background-color: #aaa;
}
</style>
